// ============================================================================
// TeamDetailPage — fiche equipe (E1b, F-216..F-224) — /equipes/:id
// ============================================================================
// Portage du PoC (dashboard)/equipes/[id]/page.tsx au standard nav maison (gabarit
// UG/E3a) : breadcrumb LABELLE « Equipes › {nom} » -> TeamSummaryHeader -> jauge
// pulse (GsKpiComparison + comparatif mois precedent) -> sections Pulses recents /
// Objectifs (CRUD) + laterale Responsable / Membres / Affichages. Detail ROUTE
// (URL /equipes/{id}). Toutes les gardes sont SERVEUR (viewer.* = affordances) :
// canWriteObjectives (M-204 director scope) et canSeePulseDetail (M-208 PII).

import { Link, useParams, useSearchParams } from 'react-router-dom';
import {
  EmptyState, QueryError, SkeletonLine, SkeletonCard, GsKpiComparison, GS_COMPONENTS,
  type GsKpiComparisonDelta,
} from '@gs/ui';
import { Activity, ChevronRight, Megaphone, ShieldAlert, UserRound, Users } from 'lucide-react';
import { ApiForbiddenError, ApiServerError } from '@gs/api-client';
import { useAuth } from '@gs/auth';
import { useTranslation } from '../../i18n';
import { useTeamDetail, type TeamDetailResponse } from '../../hooks/useTeams';
import { PulseHistory } from '../../components/equipes/PulseHistory';
import { ObjectivesManager } from '../../components/equipes/ObjectivesManager';
import { NAV_LINK } from '../accueil/RailCard';
import { TeamSummaryHeader } from './TeamSummaryHeader';
import { TeamsForbidden } from './EquipesPage';
import { formatDateOnly, formatPeriodLong, shiftPeriod } from './dates';

const KNOWN_ERRORS = ['invalid', 'forbidden', 'notfound', 'server'];
/** Plafond aperçu aside (drill-in « Voir tout » si dépassé). */
const ASIDE_PREVIEW = 5;

function Section({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <section className="space-y-3">
      <h3 className="text-body font-semibold text-slate-900 dark:text-slate-100">{title}</h3>
      {children}
    </section>
  );
}

// ─── Jauge pulse (F-220 : pulse actuel + comparatif mois precedent + moyenne 12m) ──

function PulseGauge({ detail }: { detail: TeamDetailResponse }) {
  const { t, i18n } = useTranslation();
  const pulses = detail.pulses;
  const latest = pulses[0] ?? null;
  const prev = latest ? pulses.find((p) => p.period === shiftPeriod(latest.period, -1)) ?? null : null;
  const moodDelta = latest && prev ? latest.moodScore - prev.moodScore : null;
  const avg = pulses.length > 0 ? pulses.reduce((s, p) => s + p.moodScore, 0) / pulses.length : null;

  const deltas: GsKpiComparisonDelta[] =
    moodDelta != null && moodDelta !== 0
      ? [{
          value: `${moodDelta > 0 ? '+' : ''}${moodDelta}`,
          label: t('gest:equipes.pulse.vsPrevMonth'),
          direction: moodDelta > 0 ? 'up' : 'down',
          favorable: moodDelta > 0,
        }]
      : [];

  return (
    <div className={`${GS_COMPONENTS.card} flex flex-wrap items-end justify-between gap-4 p-4`}>
      <GsKpiComparison
        label={t('gest:equipes.pulse.current')}
        value={latest ? t('gest:equipes.pulse.score', { score: latest.moodScore }) : '—'}
        deltas={deltas}
      />
      <div className="text-right">
        <p className="text-meta font-medium text-slate-500 dark:text-slate-400">{t('gest:equipes.pulse.avg12')}</p>
        <p className="mt-1 text-head font-bold tabular-nums text-slate-800 dark:text-slate-100">
          {avg != null
            ? t('gest:equipes.pulse.score', { score: new Intl.NumberFormat(i18n.language.startsWith('en') ? 'en-CA' : 'fr-CA', { minimumFractionDigits: 1, maximumFractionDigits: 1 }).format(avg) })
            : '—'}
        </p>
        <p className="text-chip text-slate-400">
          {latest ? t('gest:equipes.pulse.period', { period: formatPeriodLong(i18n.language, latest.period) }) : t('gest:equipes.pulse.none')}
        </p>
      </div>
    </div>
  );
}

// ─── Laterale ─────────────────────────────────────────────────────────────────

function AsideSections({ detail }: { detail: TeamDetailResponse }) {
  const { t, i18n } = useTranslation();
  const { team, members, postings } = detail;
  const director = team.directorName ?? team.directorEmail;

  // Echeance proche (<= +7 jours) -> badge warning (parite PoC deadlineSoonLimit).
  const soonLimit = (() => {
    const d = new Date();
    d.setDate(d.getDate() + 7);
    const p = (n: number) => String(n).padStart(2, '0');
    return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())}`;
  })();

  return (
    <aside className="flex flex-col gap-6 lg:sticky lg:top-8 lg:col-span-2 lg:self-start">
      <Section title={t('gest:equipes.aside.director')}>
        {director ? (
          <div className="flex items-center gap-2.5 rounded-lg border border-slate-200 bg-white px-3 py-2.5 dark:border-slate-700 dark:bg-slate-800">
            <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-slate-100 text-slate-500 dark:bg-slate-700 dark:text-slate-300">
              <UserRound size={16} aria-hidden="true" />
            </span>
            <span className="min-w-0">
              <p className="truncate text-field font-medium text-slate-800 dark:text-slate-100">{director}</p>
              {team.directorEmail && team.directorName ? (
                <p className="truncate text-meta text-slate-500 dark:text-slate-400">{team.directorEmail}</p>
              ) : null}
            </span>
          </div>
        ) : (
          <p className="text-field text-slate-500 dark:text-slate-400">{t('gest:equipes.aside.noDirector')}</p>
        )}
      </Section>

      <Section title={t('gest:equipes.aside.members', { n: members.length })}>
        {members.length === 0 ? (
          <EmptyState icon={<Users size={40} aria-hidden="true" />} title={t('gest:equipes.aside.noMembers')} />
        ) : (
          <>
            <ul className="flex flex-col gap-2">
              {members.slice(0, ASIDE_PREVIEW).map((m) => (
                <li key={m.email} className="flex items-center justify-between gap-2 rounded-lg border border-slate-200 bg-white px-3 py-2 dark:border-slate-700 dark:bg-slate-800">
                  <span className="min-w-0">
                    <p className="truncate text-field text-slate-800 dark:text-slate-100">{m.fullName ?? m.email}</p>
                    {m.jobTitle ? <p className="truncate text-meta text-slate-500 dark:text-slate-400">{m.jobTitle}</p> : null}
                  </span>
                  {m.isDirector ? (
                    <span className="shrink-0 rounded-full border border-slate-200 px-2 py-0.5 text-chip font-medium text-slate-500 dark:border-slate-600 dark:text-slate-400">
                      {t('gest:equipes.aside.directorBadge')}
                    </span>
                  ) : null}
                </li>
              ))}
            </ul>
            {members.length > ASIDE_PREVIEW ? (
              <div className="mt-2.5 border-t border-slate-100 pt-2.5 dark:border-slate-700/60">
                <Link to="/organigramme" className={NAV_LINK}>
                  {t('gest:equipes.aside.viewAllMembers', { count: members.length })}
                </Link>
              </div>
            ) : null}
          </>
        )}
      </Section>

      <Section title={t('gest:equipes.aside.postings')}>
        {postings.length === 0 ? (
          <EmptyState icon={<Megaphone size={40} aria-hidden="true" />} title={t('gest:equipes.aside.noPostings')} />
        ) : (
          <>
            <ul className="flex flex-col gap-2">
              {postings.slice(0, ASIDE_PREVIEW).map((p) => (
                <li key={p.id}>
                  <Link
                    to={`/affichages/${p.id}`}
                    className="flex items-center justify-between gap-2 rounded-lg border border-slate-200 bg-white px-3 py-2 transition-colors hover:border-slate-300 dark:border-slate-700 dark:bg-slate-800 dark:hover:border-slate-500"
                  >
                    <span className="min-w-0">
                      <p className="truncate text-field text-slate-800 dark:text-slate-100">{p.title}</p>
                      {p.description ? <p className="truncate text-meta text-slate-500 dark:text-slate-400">{p.description}</p> : null}
                    </span>
                    {p.deadline ? (
                      <span className={`shrink-0 rounded-full px-2 py-0.5 text-chip font-medium ${
                        p.deadline <= soonLimit
                          ? 'bg-amber-100 text-amber-800 dark:bg-amber-950/40 dark:text-amber-300'
                          : 'bg-slate-100 text-slate-600 dark:bg-slate-700 dark:text-slate-300'
                      }`}>
                        {formatDateOnly(i18n.language, p.deadline)}
                      </span>
                    ) : null}
                  </Link>
                </li>
              ))}
            </ul>
            {postings.length > ASIDE_PREVIEW ? (
              <div className="mt-2.5 border-t border-slate-100 pt-2.5 dark:border-slate-700/60">
                <Link to="/affichages" className={NAV_LINK}>
                  {t('gest:equipes.aside.viewAllPostings', { count: postings.length })}
                </Link>
              </div>
            ) : null}
          </>
        )}
      </Section>
    </aside>
  );
}

// ─── Page ────────────────────────────────────────────────────────────────────

function DetailSkeleton() {
  return (
    <div className="space-y-4">
      <SkeletonLine className="h-4 w-64" />
      <SkeletonCard />
      <SkeletonCard />
    </div>
  );
}

export default function TeamDetailPage() {
  const { t } = useTranslation();
  const { id } = useParams<{ id: string }>();
  const { user } = useAuth();
  const [searchParams] = useSearchParams();

  const detailQ = useTeamDetail(id, !!user);
  const errorCode = searchParams.get('error');

  const notFound = detailQ.isError && detailQ.error instanceof ApiServerError && detailQ.error.status === 404;
  const forbidden = detailQ.isError && detailQ.error instanceof ApiForbiddenError;

  return (
    <div className="space-y-5">
      {/* Fil d'Ariane LABELLE « Equipes › {nom} » — bloc canonique UI_STANDARDS §Pattern fiche. */}
      <nav aria-label={t('gest:equipes.breadcrumbLabel')} className="mb-2 flex items-center gap-1.5 text-meta text-slate-500">
        <Link to="/equipes" className="touch-manipulation font-medium text-slate-500 transition-colors hover:text-slate-700 dark:hover:text-slate-300">
          {t('gest:equipes.title')}
        </Link>
        <ChevronRight size={14} aria-hidden className="text-slate-300 dark:text-slate-600" />
        <span aria-current="page" className="truncate font-semibold text-slate-800 dark:text-slate-100">
          {detailQ.data?.team.name ?? '…'}
        </span>
      </nav>

      {errorCode ? (
        <div role="alert" className="rounded-xl border border-red-200 bg-red-50 px-4 py-3 text-field text-red-700 dark:border-red-900 dark:bg-red-950/40 dark:text-red-300">
          {t(`gest:equipes.errors.${KNOWN_ERRORS.includes(errorCode) ? errorCode : 'server'}`)}
        </div>
      ) : null}

      {detailQ.isLoading ? (
        <DetailSkeleton />
      ) : notFound ? (
        <EmptyState
          icon={<ShieldAlert size={48} aria-hidden="true" />}
          title={t('gest:equipes.notFound.title')}
          description={t('gest:equipes.notFound.description')}
          action={<Link to="/equipes" className={GS_COMPONENTS.btnSecondary}>{t('gest:equipes.notFound.back')}</Link>}
        />
      ) : forbidden ? (
        <TeamsForbidden />
      ) : detailQ.isError ? (
        <QueryError message={t('gest:equipes.loadError')} onRetry={() => void detailQ.refetch()} />
      ) : detailQ.data ? (
        <>
          <TeamSummaryHeader
            name={detailQ.data.team.name}
            department={detailQ.data.team.department}
            headcount={detailQ.data.team.headcount}
            directorName={detailQ.data.team.directorName}
            directorEmail={detailQ.data.team.directorEmail}
            memberCount={detailQ.data.members.length}
            objectivesDone={detailQ.data.objectives.filter((o) => o.status === 'done').length}
            objectivesTotal={detailQ.data.objectives.length}
            postingsCount={detailQ.data.postings.length}
          />

          <PulseGauge detail={detailQ.data} />

          <div className="grid gap-8 lg:grid-cols-5 lg:gap-6">
            <div className="flex flex-col gap-8 lg:col-span-3">
              <Section title={t('gest:equipes.sections.pulses')}>
                {detailQ.data.pulses.length === 0 ? (
                  <EmptyState icon={<Activity size={48} aria-hidden="true" />} title={t('gest:equipes.pulse.empty')} />
                ) : (
                  <PulseHistory pulses={detailQ.data.pulses} canSeeDetail={detailQ.data.viewer.canSeePulseDetail} />
                )}
              </Section>

              <ObjectivesManager
                key={detailQ.data.team.id}
                teamId={detailQ.data.team.id}
                year={detailQ.data.year}
                initial={detailQ.data.objectives}
                canWrite={detailQ.data.viewer.canWriteObjectives}
              />
            </div>

            <AsideSections detail={detailQ.data} />
          </div>
        </>
      ) : null}
    </div>
  );
}
